import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export interface RecentItem {
  id: string;
  name: string;
  type: 'ENTITY' | 'PROJECT' | 'TASK' | 'TASK_GROUP' | 'CUSTOM_COLLECTION';
  identifier?: string;      // Task number like "YOLK-123"
  parentName?: string;      // Project/entity name for context
  organizationId: string;   // Scope items to organization
  visitedAt: number;        // Timestamp for sorting
}

interface RecentItemsState {
  items: RecentItem[];
  maxItems: number;

  // Actions
  addRecentItem: (item: Omit<RecentItem, 'visitedAt'>) => void;
  getRecentItems: (organizationId: string, types?: ('ENTITY' | 'PROJECT' | 'TASK' | 'TASK_GROUP' | 'CUSTOM_COLLECTION')[], limit?: number) => RecentItem[];
  clearRecentItems: (organizationId?: string) => void;
  removeRecentItem: (id: string) => void;
}

export const useRecentItemsStore = create<RecentItemsState>()(
  devtools(
    persist(
      (set, get) => ({
        items: [],
        maxItems: 50, // Store more, display fewer

        addRecentItem: (item) => {
          set((state) => {
            // Remove existing item with same id and type (to move it to top)
            const filteredItems = state.items.filter(
              (existing) => !(existing.id === item.id && existing.type === item.type)
            );

            // Add new item at the beginning
            const newItem: RecentItem = {
              ...item,
              visitedAt: Date.now(),
            };

            // Keep only maxItems
            const updatedItems = [newItem, ...filteredItems].slice(0, state.maxItems);

            return { items: updatedItems };
          });
        },

        getRecentItems: (organizationId, types, limit = 10) => {
          const state = get();

          let filtered = state.items.filter(
            (item) => item.organizationId === organizationId
          );

          // Filter by types if specified
          if (types && types.length > 0) {
            filtered = filtered.filter((item) => types.includes(item.type));
          }

          // Sort by visitedAt (most recent first) and limit
          return filtered
            .sort((a, b) => b.visitedAt - a.visitedAt)
            .slice(0, limit);
        },

        clearRecentItems: (organizationId) => {
          set((state) => {
            if (organizationId) {
              // Clear only items for specific organization
              return {
                items: state.items.filter(
                  (item) => item.organizationId !== organizationId
                ),
              };
            }
            // Clear all items
            return { items: [] };
          });
        },

        removeRecentItem: (id) => {
          set((state) => ({
            items: state.items.filter((item) => item.id !== id),
          }));
        },
      }),
      {
        name: 'recent-items-store',
        version: 1,
      }
    ),
    { name: 'RecentItemsStore' }
  )
);
